import "./quoting-accordion.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { ExpansionPanelActionEvent } from "@tql/ui-components-react/kendo/layout";
import {
	ExpansionPanel,
	ExpansionPanelContent,
} from "@tql/ui-components-react/kendo/layout";
import { Reveal } from "@tql/ui-components-react/tql/animation";
import {
	chevronRight,
	chevronUp,
} from "@tql/ui-components-react/untitled/icons";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

export type Step = {
	step: number;
	title: string;
	id: string;
	active: boolean;
	completed: boolean;
	completedHeader?: ReactNode;
	header?: ReactNode;
	content: ReactNode;
	completedContent?: ReactNode;
};

type TitleProps = {
	step: Step;
	isExpanded: boolean;
};

const Title = ({ step, isExpanded }: TitleProps) => {
	return (
		<div className={"step-title-container"}>
			{isExpanded ? (
				<SvgIcon icon={chevronUp} size={"xlarge"} />
			) : (
				<SvgIcon icon={chevronRight} size={"xlarge"} />
			)}
			<div>
				<div className={"step-step"}>{step.step}</div>
			</div>

			<div className={"step-title"}>
				<h4>{step.title}</h4>
			</div>
		</div>
	);
};

type QuotingAccordionProps = {
	steps: Step[];
};

// Kendo's ExpansionPanel preventDefaults any Enter/Space reaching it, which cancels activation of controls inside the panel.
const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
	if (e.key === " " || e.key === "Enter") {
		e.stopPropagation();
	}
};

export const QuotingAccordion = ({ steps }: QuotingAccordionProps) => {
	const [expandedPanels, setExpandedPanels] = useState<Set<string>>(
		new Set(steps.filter((step) => step.active).map((step) => step.id)),
	);
	// Track panels the user has manually expanded (vs. those expanded because
	// they are the active step). This lets us preserve user expansions across
	// re-renders (e.g. async loading) without auto-expanding a step the user
	// never opened — like the previously-active step that just completed.
	const userExpandedRef = useRef<Set<string>>(new Set());

	// Update expanded panels when steps change
	useEffect(() => {
		const validExpandableIds = new Set(
			steps
				.filter((step) => step.active || step.completed)
				.map((step) => step.id),
		);

		// Drop any user-expanded ids for steps that no longer exist or are no
		// longer expandable.
		for (const id of userExpandedRef.current) {
			if (!validExpandableIds.has(id)) {
				userExpandedRef.current.delete(id);
			}
		}

		setExpandedPanels(() => {
			const newSet = new Set<string>();

			// Always keep the active step expanded.
			for (const step of steps) {
				if (step.active) {
					newSet.add(step.id);
				}
			}

			// Preserve any panels the user manually opened.
			for (const id of userExpandedRef.current) {
				newSet.add(id);
			}

			return newSet;
		});
	}, [steps]);

	const handlePanelAction = (
		event: ExpansionPanelActionEvent,
		stepId: string,
		step: Step,
	) => {
		if (step.completed) {
			if (event.expanded) {
				userExpandedRef.current.delete(stepId);
			} else {
				userExpandedRef.current.add(stepId);
			}
			setExpandedPanels((prev) => {
				const newSet = new Set(prev);
				if (event.expanded) {
					newSet.delete(stepId);
				} else {
					newSet.add(stepId);
				}
				return newSet;
			});
		}
	};

	const isPanelExpanded = (step: Step): boolean => {
		if (step.active) {
			return true;
		}
		if (step.completed) {
			return expandedPanels.has(step.id);
		}
		return false;
	};

	return (
		<div className={"quoting-accordion-wrapper"} role={"region"}>
			{steps.map((item) => (
				<ExpansionPanel
					key={item.id}
					className={item.completed ? "completed" : ""}
					disabled={!item.completed && !item.active}
					expanded={isPanelExpanded(item)}
					onAction={(event: ExpansionPanelActionEvent) => {
						handlePanelAction(event, item.id, item);
					}}
					subtitle={item.completed ? item.completedHeader : item.header}
					// tabIndex={0}
					title={<Title isExpanded={isPanelExpanded(item)} step={item} />}
				>
					<Reveal>
						{isPanelExpanded(item) && (
							<ExpansionPanelContent>
								<div onKeyDown={onKeyDown} role={"presentation"}>
									{item.completed && item.completedContent
										? item.completedContent
										: item.content}
								</div>
							</ExpansionPanelContent>
						)}
					</Reveal>
				</ExpansionPanel>
			))}
		</div>
	);
};
